import { useEffect, useRef, useState } from 'react';

/**
 * Its own `fbx-` styles, declared here: a class in this app is live only while the component that declares it is mounted (there are no
 * CSS Modules), so this does not lean on My feedback's rules.
 */
const css = `
  .fbx { display: flex; flex-direction: column; gap: 6px; padding-top: 4px; }
  .fbx-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .fbx-ask { margin: 0; font: var(--ew-fw-regular) 12.5px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fbx-err { margin: 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-danger); }
`;

export interface StopFollowingProps {
  /** A few words about the item, so a list of identical buttons is not a list of identical names to somebody using a screen reader. */
  about?: string;
  /** The request is on its way: both buttons are off so it cannot be sent twice. */
  pending?: boolean;
  /** Why the last attempt did not go through, in words for a person. */
  error?: string | null;
  onStop: () => void;
  /** The person starts over, or backs out: whatever the last attempt said is about an attempt that is over, so the owner of `error` forgets it. */
  onReset?: () => void;
}

/**
 * "Stop following", for an item I follow because I said "me too" (the API decides which, and says so in `canStopFollowing`: the follow that
 * comes with sending an item cannot be undone). TWO STEPS, because it is not always reversible: if the item is still open on the board I can
 * say "me too" again, but once it is closed or taken off the board there is no way back, and a stray tap in a list of my items must not do it.
 *
 * The control decides nothing about who may; it is shown only where the API will do it, and what comes back is shown as it is.
 */
export default function StopFollowingControl({ about, pending = false, error = null, onStop, onReset }: StopFollowingProps) {
  const [asking, setAsking] = useState(false);
  const label = (verb: string) => (about ? `${verb}: ${about}` : verb);
  // ⚠️ WHAT A PRESS DOES TO THE PAGE. Each step REPLACES the button that was pressed (and a button that was pressed and then turned off, while
  // the request is on its way, lets go of focus too), and a button that leaves the page takes focus with it, to the top of the document: a
  // keyboard or screen-reader user would be back at the top of the drawer after every step. So the step decides where focus goes next.
  const rootRef = useRef<HTMLDivElement>(null);
  const askRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const focusNext = useRef<'ask' | 'confirm' | null>(null);
  // A refusal: the confirmation comes back on, and it was off (and without focus) while the request was on its way.
  useEffect(() => {
    if (error && asking) focusNext.current = 'confirm';
  // eslint-disable-next-line react-hooks/exhaustive-deps -- it is the error arriving that this answers
  }, [error]);
  // Done after every render until it has been, but only once the button is ON (one that is off cannot take focus), and ONLY IF FOCUS WAS LOST
  // OR IS STILL HERE: somebody who has moved on while it was on its way must not have focus pulled out from under them.
  useEffect(() => {
    if (!focusNext.current || pending) return;
    const where = document.activeElement;
    const lostOrHere = !where || where === document.body || !!rootRef.current?.contains(where);
    if (lostOrHere) (focusNext.current === 'confirm' ? confirmRef : askRef).current?.focus();
    focusNext.current = null;
  });
  const startAsking = () => { focusNext.current = 'confirm'; onReset?.(); setAsking(true); };
  const keepFollowing = () => { focusNext.current = 'ask'; onReset?.(); setAsking(false); };
  return (
    <div className="fbx" ref={rootRef}>
      <style>{css}</style>
      {asking ? (
        <>
          <p className="fbx-ask">
            You won’t be told about this one any more, and it will leave this list. If it is still open on the board, you can say “me too” to it
            again from Everyone’s feedback.
          </p>
          <div className="fbx-row">
            {/* The name says what is happening, because the label stands in for the visible words: "Stopping…" would otherwise never be heard. */}
            <button
              type="button" className="btn btn-primary" style={{ padding: '4px 12px' }}
              ref={confirmRef} disabled={pending} aria-label={label(pending ? 'Stopping' : 'Stop following')} onClick={onStop}
            >
              {pending ? 'Stopping…' : 'Stop following'}
            </button>
            <button type="button" className="btn btn-secondary" style={{ padding: '4px 12px' }} disabled={pending} onClick={keepFollowing}>
              Keep following
            </button>
          </div>
        </>
      ) : (
        <div className="fbx-row">
          <button type="button" className="btn btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }} ref={askRef} aria-label={label('Stop following…')} onClick={startAsking}>
            Stop following…
          </button>
        </div>
      )}
      {/* Only at the step it is about: "Keep following" backs out of the attempt, and an error about one that was abandoned is not left behind. */}
      {asking && error && <p className="fbx-err" role="alert">{error}</p>}
    </div>
  );
}
